/**
 * SidebarStep5_FutureClosure - Expense Continuation & One-Time Costs (Future Closure Path)
 * 
 * This component allows users to configure:
 *   - Which expenses continue during closure (rent, insurance, etc.)
 *   - Shutdown costs
 *   - Restart costs
 * 
 * Shows when calculation_mode === 'future_closure'
 */

import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Info, 
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Building,
  Zap,
  Users,
  Shield,
  Wrench,
  RotateCcw,
  Loader2
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { Slider } from '@/components/ui/slider';

interface SidebarStep5FutureClosureProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  goToStep?: (step: number) => void;
  highestCompletedStep?: number;
  claimId?: string;
}

// Default continuation percentages by expense type
const DEFAULT_CONTINUATIONS = {
  officer_salary: 100,
  employee_wages: 0,
  rent: 100,
  utilities: 25,
  insurance: 90,
  loan_payments: 100,
  other_fixed: 30
};

export default function SidebarStep5_FutureClosure({ 
  onNext, 
  onBack, 
  data, 
  onDataUpdate, 
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  highestCompletedStep,
  claimId
}: SidebarStep5FutureClosureProps) {
  // Expense continuation percentages (what % continues during closure)
  const [continuations, setContinuations] = useState({
    officer_salary: data.expense_continuations?.officer_salary ?? DEFAULT_CONTINUATIONS.officer_salary,
    employee_wages: data.expense_continuations?.employee_wages ?? DEFAULT_CONTINUATIONS.employee_wages,
    rent: data.expense_continuations?.rent ?? DEFAULT_CONTINUATIONS.rent,
    utilities: data.expense_continuations?.utilities ?? DEFAULT_CONTINUATIONS.utilities,
    insurance: data.expense_continuations?.insurance ?? DEFAULT_CONTINUATIONS.insurance,
    loan_payments: data.expense_continuations?.loan_payments ?? DEFAULT_CONTINUATIONS.loan_payments,
    other_fixed: data.expense_continuations?.other_fixed ?? DEFAULT_CONTINUATIONS.other_fixed
  });

  // One-time costs
  const [shutdownCosts, setShutdownCosts] = useState({
    equipment_securing: data.shutdown_costs?.equipment_securing || 0,
    inventory_protection: data.shutdown_costs?.inventory_protection || 0,
    employee_severance: data.shutdown_costs?.employee_severance || 0,
    lease_termination: data.shutdown_costs?.lease_termination || 0,
    other_shutdown: data.shutdown_costs?.other_shutdown || 0
  });

  const [restartCosts, setRestartCosts] = useState({
    equipment_repair: data.restart_costs?.equipment_repair || 0,
    inventory_replenishment: data.restart_costs?.inventory_replenishment || 0,
    rehiring_training: data.restart_costs?.rehiring_training || 0,
    marketing_relaunch: data.restart_costs?.marketing_relaunch || 0,
    other_restart: data.restart_costs?.other_restart || 0
  });

  // Calculate totals
  const totalShutdownCosts = Object.values(shutdownCosts).reduce((sum, val) => sum + (val || 0), 0);
  const totalRestartCosts = Object.values(restartCosts).reduce((sum, val) => sum + (val || 0), 0);
  const totalOneTimeCosts = totalShutdownCosts + totalRestartCosts;

  // Estimate monthly continuing expenses from Step 4 fixed expenses
  const monthlyFixedExpenses = data.fixed_expenses || {};
  const estimatedMonthlyContinuing = 
    (monthlyFixedExpenses.officer_salary || 0) * (continuations.officer_salary / 100) +
    (monthlyFixedExpenses.rent || 0) * (continuations.rent / 100) +
    (monthlyFixedExpenses.utilities || 0) * (continuations.utilities / 100) +
    (monthlyFixedExpenses.insurance || 0) * (continuations.insurance / 100) +
    (monthlyFixedExpenses.other_fixed || 0) * (continuations.other_fixed / 100);

  const closureDuration = data.closure_duration_months || data.future_closure_params?.closure_duration_months || 36;
  const totalContinuingExpenses = estimatedMonthlyContinuing * closureDuration;

  // Handle continuation change
  const handleContinuationChange = (field: string, value: number) => {
    setContinuations(prev => ({
      ...prev,
      [field]: Math.min(100, Math.max(0, value))
    }));
  };

  // Handle shutdown cost change
  const handleShutdownChange = (field: string, value: number) => {
    setShutdownCosts(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle restart cost change
  const handleRestartChange = (field: string, value: number) => {
    setRestartCosts(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply default continuations
  const applyDefaults = () => {
    setContinuations(DEFAULT_CONTINUATIONS);
  };

  // Handle next
  const handleNext = () => {
    onNext({
      expense_continuations: continuations,
      shutdown_costs: shutdownCosts,
      restart_costs: restartCosts,
      total_continuing_expenses: totalContinuingExpenses,
      total_one_time_costs: totalOneTimeCosts,
    });
  };

  // Calculate completion
  const completionPercent = 85;
  const hasConfiguredExpenses = Object.values(continuations).some(v => v !== DEFAULT_CONTINUATIONS[Object.keys(DEFAULT_CONTINUATIONS)[0] as keyof typeof DEFAULT_CONTINUATIONS]);
  const grade = totalOneTimeCosts > 0 || hasConfiguredExpenses ? 'B' : 'C';

  // Build sidebar sections
  const buildSections = () => [
    {
      id: 'continuations',
      label: 'Expense Continuation',
      isActive: true,
      isComplete: true,
      onClick: () => document.getElementById('continuations-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'shutdown',
      label: 'Shutdown Costs',
      isActive: false,
      isComplete: totalShutdownCosts > 0,
      onClick: () => document.getElementById('shutdown-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'restart',
      label: 'Restart Costs',
      isActive: false,
      isComplete: totalRestartCosts > 0,
      onClick: () => document.getElementById('restart-section')?.scrollIntoView({ behavior: 'smooth' })
    }
  ];

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    items.push({ id: 'continuations', label: 'Continuations set', status: 'ok' });
    
    if (totalShutdownCosts > 0) {
      items.push({ id: 'shutdown', label: `Shutdown: $${totalShutdownCosts.toLocaleString()}`, status: 'ok' });
    }
    
    if (totalRestartCosts > 0) {
      items.push({ id: 'restart', label: `Restart: $${totalRestartCosts.toLocaleString()}`, status: 'ok' });
    }
    
    return items;
  };

  // Expense continuation row component
  const ExpenseRow = ({ 
    label, 
    icon: Icon, 
    field, 
    description 
  }: { 
    label: string; 
    icon: any; 
    field: keyof typeof continuations;
    description: string;
  }) => {
    const value = continuations[field];
    
    // Color based on value
    const getValueColor = (val: number) => {
      if (val >= 80) return 'text-green-600';
      if (val >= 50) return 'text-amber-600';
      return 'text-gray-600';
    };
    
    return (
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition">
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-xs text-gray-500 truncate">{description}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-32">
            <Slider
              value={[value]}
              onValueChange={(vals) => handleContinuationChange(field, vals[0])}
              min={0}
              max={100}
              step={5}
              className="cursor-pointer"
            />
          </div>
          <div className="w-14 text-right">
            <span className={`text-lg font-bold ${getValueColor(value)}`}>{value}%</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <WizardV2Layout
      currentStep={5}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Expense Continuation & One-Time Costs"
      stepSubtitle={`Configure expenses for ${closureDuration}-month closure projection`}
      completionPercent={completionPercent}
      grade={grade}
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleNext}
      claimId={claimId}
      nextLabel="Continue to Pro Forma Review"
      isNextDisabled={false}
      showNext={true}
    >
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* Summary Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-blue-200 text-sm">Closure Duration</p>
              <p className="text-3xl font-bold">{closureDuration} months</p>
            </div>
            <div>
              <p className="text-blue-200 text-sm">Est. Continuing Expenses</p>
              <p className="text-3xl font-bold">${totalContinuingExpenses.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-blue-200 text-sm">One-Time Costs</p>
              <p className="text-3xl font-bold">${totalOneTimeCosts.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Expense Continuation Section */}
        <div id="continuations-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Expense Continuation Rates</h3>
                <p className="text-sm text-gray-500">What % of each expense continues during closure?</p>
              </div>
            </div>
            <button
              onClick={applyDefaults}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Reset to Defaults
            </button>
          </div>

          <div className="space-y-3">
            <ExpenseRow 
              label="Officer/Owner Salary" 
              icon={Users}
              field="officer_salary"
              description="Typically 100% - owners still need income"
            />
            <ExpenseRow 
              label="Employee Wages" 
              icon={Users}
              field="employee_wages"
              description="Often 0% during full closure, or partial for retention"
            />
            <ExpenseRow 
              label="Rent/Lease" 
              icon={Building}
              field="rent"
              description="Usually 100% - lease obligations continue"
            />
            <ExpenseRow 
              label="Utilities" 
              icon={Zap}
              field="utilities"
              description="Reduced usage, but base charges continue"
            />
            <ExpenseRow 
              label="Insurance" 
              icon={Shield}
              field="insurance"
              description="Typically 90-100% - coverage must be maintained"
            />
            <ExpenseRow 
              label="Loan Payments" 
              icon={DollarSign}
              field="loan_payments"
              description="100% - debt obligations continue"
            />
            <ExpenseRow 
              label="Other Fixed" 
              icon={Building}
              field="other_fixed"
              description="Subscriptions, maintenance contracts, etc."
            />
          </div>

          {/* Monthly estimate */}
          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Estimated Monthly Continuing Expenses</span>
              </div>
              <span className="text-xl font-bold text-blue-900">${estimatedMonthlyContinuing.toLocaleString()}/mo</span>
            </div>
          </div>
        </div>

        {/* Shutdown Costs Section */}
        <div id="shutdown-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Wrench className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Shutdown Costs</h3>
              <p className="text-sm text-gray-500">One-time costs to close operations</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Securing</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={shutdownCosts.equipment_securing || ''}
                  onChange={(e) => handleShutdownChange('equipment_securing', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Protection</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={shutdownCosts.inventory_protection || ''}
                  onChange={(e) => handleShutdownChange('inventory_protection', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee Severance</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={shutdownCosts.employee_severance || ''}
                  onChange={(e) => handleShutdownChange('employee_severance', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lease Termination Fees</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={shutdownCosts.lease_termination || ''}
                  onChange={(e) => handleShutdownChange('lease_termination', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Other Shutdown Costs</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={shutdownCosts.other_shutdown || ''}
                  onChange={(e) => handleShutdownChange('other_shutdown', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          {/* Total */}
          {totalShutdownCosts > 0 && (
            <div className="mt-4 p-4 bg-red-50 rounded-xl flex items-center justify-between">
              <span className="font-medium text-red-800">Total Shutdown Costs</span>
              <span className="text-xl font-bold text-red-900">${totalShutdownCosts.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Restart Costs Section */}
        <div id="restart-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Restart Costs</h3>
              <p className="text-sm text-gray-500">One-time costs to resume operations</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Repair/Replacement</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={restartCosts.equipment_repair || ''}
                  onChange={(e) => handleRestartChange('equipment_repair', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Replenishment</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={restartCosts.inventory_replenishment || ''}
                  onChange={(e) => handleRestartChange('inventory_replenishment', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rehiring & Training</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={restartCosts.rehiring_training || ''}
                  onChange={(e) => handleRestartChange('rehiring_training', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Marketing Relaunch</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={restartCosts.marketing_relaunch || ''}
                  onChange={(e) => handleRestartChange('marketing_relaunch', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Other Restart Costs</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={restartCosts.other_restart || ''}
                  onChange={(e) => handleRestartChange('other_restart', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          </div>

          {/* Total */}
          {totalRestartCosts > 0 && (
            <div className="mt-4 p-4 bg-green-50 rounded-xl flex items-center justify-between">
              <span className="font-medium text-green-800">Total Restart Costs</span>
              <span className="text-xl font-bold text-green-900">${totalRestartCosts.toLocaleString()}</span>
            </div>
          )}
        </div>

      </div>
    </WizardV2Layout>
  );
}
